/**
 * LƯƠNG TẠM TÍNH — with the full breakdown.
 *
 * Every line is a field the API actually returned; nothing is recomputed on the
 * device. That matters because `provisionalPay()` does all of its arithmetic in
 * integer minor units through `divRoundHalfUp`, and a phone recalculating the
 * same figures in floating point would eventually disagree with the payslip by
 * a đồng — which is exactly the kind of discrepancy that destroys trust in a
 * payroll screen.
 *
 * The word "tạm tính" is repeated deliberately: this figure excludes KPI bonus
 * and any discretionary adjustment, which only exist once a payroll run
 * confirms them.
 */
import { usePayrollPreview, errorMessage } from '../lib/queries';
import { ApiError } from '@sonauto/api-client';
import { formatMinor, formatMinutes } from '../lib/format';
import { Card, EmptyState, ErrorState, KeyValue, LoadingState, MoneyText } from '../components/ui';
import { ScreenHeader } from '../components/ScreenHeader';

export function PayrollPreviewScreen(): JSX.Element {
  const { data, isLoading, isError, error, refetch } = usePayrollPreview();

  if (isLoading) return <LoadingState label="Đang tính lương tạm tính…" />;

  // A 404 here means "no open payroll period for this branch" — a legitimate
  // state, not a failure, so it gets its own explanation.
  if (isError && error instanceof ApiError && error.code === 'NOT_FOUND') {
    return (
      <div>
        <ScreenHeader title="Lương tạm tính" />
        <EmptyState
          title="Chưa có kỳ lương đang mở"
          description="Chi nhánh của bạn chưa mở kỳ lương nào bao gồm ngày hôm nay. Khi kế toán mở kỳ lương, số liệu tạm tính sẽ hiện ở đây."
        />
      </div>
    );
  }

  if (isError || data === undefined) {
    return (
      <div>
        <ScreenHeader title="Lương tạm tính" />
        <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
      </div>
    );
  }

  const currency = data.currency;
  const hasProfile = data.baseAmountMinor > 0 || data.hourlyEquivalentMinor !== undefined;

  return (
    <div className="pb-8">
      <ScreenHeader title="Lương tạm tính" subtitle={`Kỳ ${data.periodCode}`} />

      <Card>
        <p className="text-sm text-slate-500">Ước tính thực nhận kỳ này</p>
        <p className="mt-1 text-4xl font-black tabular-nums text-slate-900">
          {formatMinor(data.estimatedNetMinor, currency)}
        </p>
        <p className="mt-1 text-sm text-slate-600">
          {data.periodStart} → {data.periodEnd}
          {data.periodStatus === undefined ? '' : ` · ${data.periodStatus}`}
        </p>
      </Card>

      {/* --- what the pay is computed from -------------------------------- */}
      <p className="mb-1 mt-6 text-sm font-bold uppercase tracking-wide text-slate-500">Công đã ghi nhận</p>
      <Card className="!py-1">
        <div className="divide-y divide-slate-100">
          <KeyValue label="Số ca có công" value={data.shiftCount} />
          <KeyValue label="Giờ làm" value={formatMinutes(data.workedMinutes)} />
          <KeyValue label="Tăng ca" value={formatMinutes(data.overtimeMinutes)} />
          {data.nightMinutes === undefined ? null : (
            <KeyValue label="Giờ đêm" value={formatMinutes(data.nightMinutes)} />
          )}
          {data.holidayMinutes === undefined ? null : (
            <KeyValue label="Giờ ngày lễ" value={formatMinutes(data.holidayMinutes)} />
          )}
          {data.lateMinutes === undefined ? null : (
            <KeyValue label="Đi muộn" value={formatMinutes(data.lateMinutes)} />
          )}
        </div>
      </Card>

      {/* --- the money breakdown ------------------------------------------- */}
      <p className="mb-1 mt-6 text-sm font-bold uppercase tracking-wide text-slate-500">Diễn giải tiền</p>
      <Card className="!py-1">
        <div className="divide-y divide-slate-100">
          {data.salaryType === undefined ? null : <KeyValue label="Hình thức lương" value={data.salaryType} />}
          {data.hourlyEquivalentMinor === undefined ? null : (
            <KeyValue
              label="Đơn giá quy đổi theo giờ"
              hint="Dùng chung cho tăng ca, giờ đêm và ngày lễ"
              value={<MoneyText amountMinor={data.hourlyEquivalentMinor} currency={currency} />}
            />
          )}
          <KeyValue
            label="Lương cơ bản theo công"
            value={<MoneyText amountMinor={data.baseAmountMinor} currency={currency} />}
          />
          <KeyValue
            label="Phụ trội tăng ca"
            value={<MoneyText amountMinor={data.overtimeAmountMinor} currency={currency} />}
          />
          <KeyValue
            label="Phụ trội giờ đêm"
            value={<MoneyText amountMinor={data.nightAmountMinor} currency={currency} />}
          />
          <KeyValue
            label="Phụ trội ngày lễ"
            value={<MoneyText amountMinor={data.holidayAmountMinor} currency={currency} />}
          />
          <KeyValue
            label="Tổng trước khấu trừ"
            value={
              <span className="text-lg">
                <MoneyText amountMinor={data.estimatedGrossMinor} currency={currency} />
              </span>
            }
          />
          <KeyValue
            label="Trừ tiền đã ứng"
            hint="Các khoản ứng đã duyệt hoặc đã chi, chưa hoàn"
            value={
              <span className="text-rose-700">
                −<MoneyText amountMinor={data.advanceDeductionMinor} currency={currency} />
              </span>
            }
          />
          <KeyValue
            label="Ước tính thực nhận"
            value={
              <span className="text-lg text-brand-700">
                <MoneyText amountMinor={data.estimatedNetMinor} currency={currency} />
              </span>
            }
          />
        </div>
      </Card>

      {!hasProfile ? (
        <p className="mt-3 rounded-2xl bg-amber-50 p-3 text-sm text-amber-900">{data.note}</p>
      ) : (
        <p className="mt-3 rounded-2xl bg-slate-100 p-3 text-sm text-slate-600">{data.note}</p>
      )}

      <p className="mt-3 rounded-2xl bg-slate-100 p-3 text-xs leading-relaxed text-slate-600">
        Con số này được tính từ dữ liệu chấm công đã ghi nhận đến thời điểm hiện tại. Nếu bạn thấy số giờ làm
        chưa đúng, hãy kiểm tra <strong>Lịch sử chấm công</strong> — một lần chấm công đang chờ xác minh sẽ
        chưa được tính vào đây.
      </p>
    </div>
  );
}
